import React, { useState, useRef, useEffect, useMemo } from "react";
import { InputGroup, Form, Button } from "react-bootstrap";
import AutoCompleteItem from "./AutoCompleteItem";

const AutoComplete = ({
  data = [],
  onSelect,
  viewOnly = false,
  item = "",
  placeholder = "Type to choose category...",
  onChange,
  className = ""
}) => {
  const [isVisible, setVisibility] = useState(false);
  const [search, setSearch] = useState(item || '');
  const [cursor, setCursor] = useState(-1);

  const searchContainer = useRef(null);
  const searchResultRef = useRef(null);

  useEffect(() => {
    if (item !== undefined && item !== null) {
      setSearch(item);
    }
  }, [item]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainer.current &&
        !searchContainer.current.contains(event.target)
      ) {
        setVisibility(false);
      }
    };

    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const suggestions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    if (!search || !search.trim()) return data;

    const term = search.toLowerCase();
    return data.filter((d) => {
      const name = typeof d === 'string' ? d : d.name || '';
      return name.toLowerCase().includes(term);
    });
  }, [data, search]);

  const showSuggestion = () => {
    if (!viewOnly) setVisibility(true);
  };

  const hideSuggestion = () => {
    setVisibility(false);
    setCursor(-1);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (onChange) onChange(val);
    setVisibility(true);
    setCursor(-1);
  };

  const handleClear = () => {
    setSearch('');
    if (onChange) onChange('');
    setCursor(-1);
  };

  const handleSelectItem = (selected) => {
    const name = typeof selected === 'string' ? selected : selected.name;
    setSearch(name);
    if (onChange) onChange(name);
    hideSuggestion();
    if (onSelect) onSelect(selected);
  };

  const keyboardNavigation = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isVisible) {
        setVisibility(true);
      } else {
        setCursor((c) => (c < suggestions.length - 1 ? c + 1 : c));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => (c > 0 ? c - 1 : 0));
    } else if (e.key === "Escape") {
      hideSuggestion();
    } else if (e.key === "Enter") {
      if (isVisible && cursor >= 0 && cursor < suggestions.length) {
        e.preventDefault();
        handleSelectItem(suggestions[cursor]);
      }
    }
  };

  return (
    <div className={`position-relative w-100 ${className}`} ref={searchContainer}>
      <InputGroup>
        <InputGroup.Text className="bg-light border-end-0">
          <i className="bi bi-tag text-muted"></i>
        </InputGroup.Text>
        <Form.Control
          type="text"
          name="search"
          className="vj-search-input border-start-0"
          autoComplete="off"
          placeholder={placeholder}
          value={search}
          onClick={showSuggestion}
          onChange={handleInputChange}
          onKeyDown={keyboardNavigation}
          disabled={viewOnly}
        />
        {search && !viewOnly && (
          <Button variant="outline-secondary" onClick={handleClear}>
            <i className="bi bi-x"></i>
          </Button>
        )}
      </InputGroup>

      {isVisible && suggestions.length > 0 && (
        <div
          className="position-absolute start-0 end-0 bg-white border rounded-3 shadow-lg mt-1"
          style={{
            zIndex: 1050,
            maxHeight: "280px",
            overflowY: "auto",
            borderColor: "#cbd5e1"
          }}
        >
          <ul className="list-group list-group-flush p-0 m-0" ref={searchResultRef}>
            {suggestions.map((itemObj, idx) => {
              const name = typeof itemObj === 'string' ? itemObj : itemObj.name;
              return (
                <AutoCompleteItem
                  key={idx}
                  name={name}
                  onSelectItem={() => handleSelectItem(itemObj)}
                  isHighlighted={cursor === idx}
                />
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AutoComplete;